import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import { useAtom } from "jotai";
import "../sideMenu/style.scss";
import { useTranslation } from 'react-i18next';
import UploadMethod from "../uploadMethod/index";
import { AccordionItem } from "../AccordianItem";
import { useForm } from "@mantine/form";
import { componentsAtom } from "../../utils/atoms/componentsAtom";
import displayError from "../../constants/utils";
import MGAAPI from "../../api/index";
import _ from "lodash";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import {
  flattenCountComponentDTOList,
  flattenDesignCountComponent,
  projectDetailsAtom,
} from "../../utils/atoms/projectDetailsAtom";
import { componentStatusAtom } from "../../utils/atoms/componentStatus";
import { Grid, Switch, useMantineColorScheme, Card, Text } from "@mantine/core";
import { tourAtom } from "../../utils/atoms/tourAtom";
import { useRemoveStickyOnIntro } from "../../utils/tour/useRemoveStickyOnIntro";
import PropTypes from "prop-types";
import { helpAtom } from "../../utils/atoms/helpAtom";
import {
  mgaRegex,
  useAssessmentRouteParams,
  useCurrentDesignComponentLists,
  useDesignVersionStatusAndComponents,
  isNullOrWhitespace,
} from "../../constants/helper";
import { parseISOToLocalDateAtMidnightUTC } from "../../utils/dateHelper";
import { formatProjectNumberRange, toUtcISOStringWithStartTime } from "../../utils/helpers";
import currentEnv from "../../env";
import { getErrorMessage } from "../../utils/errorUtils";

const MGA_API = new MGAAPI();
const LoadMethod = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const {
    loadComponents,
    projectDetails,
    initialTab = "",
    initialSelectedBuildingType,
    saveComponents,
    setLoadingComp,
  } = props;
  const numberFormat = projectDetails?.projectDTO?.numberFormat;
  const formatRange = (min, max) =>
    formatProjectNumberRange(min, max, numberFormat);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const [componentStatus, setComponentStatus] = useAtom(componentStatusAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );  const getLoadInfo = useDesignVersionStatusAndComponents(
    projectDetails?.designWithVersionsDTOList,
    projectDetails?.designComponentsList
  );

  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const [componentDetails, setComponentDetails] = useAtom(componentsAtom);
  const [openSteps, setOpenSteps] = useState(false);
  const [dataSource, setDataSource] = useState(initialTab);
  const [triggerSave] = useState(false);
  const [, setProjectDetails] = useAtom(projectDetailsAtom);
  const { colorScheme } = useMantineColorScheme();
  const [tour] = useAtom(tourAtom);
  const prevDataSourceRef = useRef(dataSource);
  const previousElectricalUnitsRef = useRef(
    componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
      ?.units
  );

  const isThermalPresent = _.find(
    selectedDesignAndDesignVersion?.componentDTOList,
    {
      componentType: "ThermalLoad",
    }
  );
  const isElectricalDataPresent = _.find(
    selectedDesignAndDesignVersion?.componentDTOList,
    {
      componentType: "ElectricalLoad",
    }
  );

  const form = useForm({
    key: JSON.stringify(componentDetails?.load),
    initialValues: {
      ...componentDetails?.load,
      componentAttributes: {
        ...componentDetails?.load?.componentAttributes,
        loadParameters: {
          ...componentDetails?.load?.componentAttributes?.loadParameters,
          profileDetail: {
            ...componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail,
            startDate: componentDetails?.load?.componentAttributes
              ?.loadParameters?.profileDetail?.startDate
              ? parseISOToLocalDateAtMidnightUTC(
                  componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.startDate
                )
              : new Date(
                  new Date(
                    new Date().setDate(new Date().getDate() - 1)
                  ).setFullYear(new Date().getFullYear() - 1)
                ),
            scalingFactor:
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.scalingFactor || 1,
            profileDataLoc:
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.profileDataLoc || "OfficeMedium",
            ThermalEmissionFactor:
                  componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileDetail?.ThermalEmissionFactor || 0,
            ThermalEnergyPrice:
                  componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileDetail?.ThermalEnergyPrice || 0,
            powerFactor:
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.powerFactor || 0.85,   
            maxPowerElec:
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.maxPowerElec ?? null,
            maxPowerTherm:
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.maxPowerTherm ?? null,                         
          },
           scalingFactorElec:
              (componentDetails?.load?.componentAttributes?.loadParameters
                ?.scalingFactorElec ??
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.scalingFactor) || 1,
            scalingFactorTherm:
              (componentDetails?.load?.componentAttributes?.loadParameters
                ?.scalingFactorTherm ??
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.scalingFactor) || 1,

        },
      },
    },
    validateInputOnBlur: [
      "componentAttributes.loadParameters.profileDetail.ThermalEmissionFactor",
      "componentAttributes.loadParameters.profileDetail.ThermalEnergyPrice",
    ],
    validate: {
      componentAttributes: {
        loadParameters: {
          scalingFactorElec: (value) =>
            dataSource === "ashrae" &&
            (!value
              ? t('loadMethod.validation.scalingFactorRequiredElec')
              : !mgaRegex?.zeroPointOneToNinePointNineWithTwoDecimal?.test(String(value))
              ? t('loadMethod.validation.scalingFactorRangeElec', formatRange(0.1, 9.9))
              : null),
          scalingFactorTherm: (value) =>
            dataSource === "ashrae" &&
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.useThermalLoad &&
            (!value
              ? t('loadMethod.validation.scalingFactorRequiredTherm')
              : !mgaRegex?.zeroPointOneToNinePointNineWithTwoDecimal?.test(String(value))
              ? t('loadMethod.validation.scalingFactorRangeTherm', formatRange(0.1, 9.9))
              : null),
          profileDetail: {
            loadFile: (value) =>
              dataSource !== "ashrae" &&
              !componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad &&
              (value &&
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.isLoadFileClicked
                ? t('loadMethod.validation.loadFileClick')
                : componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileFile?.length || value
                ? null
                : t('loadMethod.validation.loadFileRequired')),
            thermalFile: (value) => {
              let isError;
              if (
                dataSource !== "ashrae" &&
                componentDetails?.load?.componentAttributes?.loadParameters
                  ?.profileDetail?.useThermalLoad
              ) {
                if (
                  (value && !isThermalPresent?.componentType) ||
                  !!isThermalPresent?.componentType
                ) {
                  if (
                    value &&
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.isThermalFileClicked
                  ) {
                    isError = t('loadMethod.validation.loadFileClick');
                  } else {
                    isError = null;
                  }
                } else if (!isThermalPresent?.componentType || !value) {
                    isError = t('loadMethod.validation.thermalFileRequired');
                } else if (
                  componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileFile?.length ||
                  value
                ) {
                  isError = null;
                }
                return isError;
              }
            },
            startDate: (value) =>
              dataSource !== "ashrae" &&
              (value ? null : t('loadMethod.validation.startDateRequired')),
            startTime: (value) =>
              dataSource !== "ashrae" &&
              (value ? null : t('loadMethod.validation.startTimeRequired')),
            timeResolution: (value) =>
              dataSource !== "ashrae" &&
              (value ? null : t('loadMethod.validation.timeResolutionRequired')),
            units: (value) =>
              dataSource !== "ashrae" && (value ? null : t('loadMethod.validation.unitsRequired')),
            profileDataLoc: (value) =>
              dataSource === "ashrae" &&
              (value ? null : t('loadMethod.validation.buildingTypeRequired')),
            maxPowerElec: (value) =>
              dataSource === "ashrae" && value !== "" && value !== null && value !== undefined &&
              (!mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
                ? t('loadMethod.validation.maxPowerRange', formatRange(0, 1000000000))
                : null),
            maxPowerTherm: (value) =>
              dataSource === "ashrae" &&
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad &&
              value !== "" && value !== null && value !== undefined &&
              (!mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(String(value))
                ? t('loadMethod.validation.maxPowerRange', formatRange(0, 1000000000))
                : null),
             ThermalEnergyPrice: (value) =>
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad &&
              (String(value).trim() === ""
                ? t("genset.validation.thermalEnergyPriceRequired")
                : !mgaRegex?.zeroToTenPower9WithDecimalRegex?.test(value)
                  ? t("genset.validation.thermalEnergyPriceRange", formatRange(0, 1000000000))
                  : null),
            ThermalEmissionFactor: (value) =>
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad &&
              (String(value).trim() === ""
                ? t("genset.validation.thermalEmissionFactorRequired")
                : !mgaRegex?.zeroTo1000WithDecimal?.test(value)
                  ? t("genset.validation.thermalEmissionFactorRange", formatRange(0, 1000))
                  : null),
            powerFactor: (value) => {
              const units = form.values?.componentAttributes?.loadParameters?.profileDetail?.units;
              const unitsRequireValidation = ['VA', 'kVA', 'VAh', 'kVAh']?.includes(units);
              if (!unitsRequireValidation) return null;
              // Fall back to current form value only when no value was supplied.
              const currentVal = value === undefined
                ? form.values?.componentAttributes?.loadParameters?.profileDetail?.powerFactor
                : value;
              if (isNullOrWhitespace(currentVal)) return t("loadMethod.validation.powerFactorRequired");
              return !mgaRegex?.zeroPointOneToOnePointZeroWithTwoDecimal?.test(String(currentVal))
                ? t("loadMethod.validation.powerFactorRange", formatRange(0.1, 1))
                : null;
            },
          },
        },
      },
    },
  });
  const { setValues } = form;

  useEffect(() => {
    if (!form.isTouched()) return;
    setComponentStatus((prev) => ({
      ...prev,
      load: {
        isEditing: true,
        completed: false,
      },
    }));
  }, [form.values]);

  const updateLoadState = (category, key, value) => {
    setComponentDetails((prev) => {
      const newState = { ...prev, currentComponent: "load" };
      if (
        key === "useThermalLoad" &&
        value &&
        componentDetails?.load?.componentAttributes?.loadParameters
          ?.profileDetail?.loadFile
      ) {
        displayError(
          "error",
          t('loadMethod.validation.saveElectricalFirst')
        );
      }else if(!key){
        _.set(newState, `load.${category}`, value);
        setComponentStatus((prev) => ({ ...prev, load: { isEditing: true, completed: false } }));
      } else {
        _.set(newState, `load.${category}.${key}`, value);
      }
      newState.load.componentAttributes.loadParameters.profileDetail.startDate =
        newState.load.componentAttributes.loadParameters.profileDetail.startDate
          ? new Date(
              newState?.load?.componentAttributes?.loadParameters?.profileDetail?.startDate
            )
          : new Date(
              new Date(
                new Date().setDate(new Date().getDate() - 1)
              ).setFullYear(new Date().getFullYear() - 1)
            );
      setValues(newState?.load);
      return newState;
    });
  };

  const handleLoadMethod = form.onSubmit((values) => {
    handleLoadUpload();
  });

  useImperativeHandle(ref, () => ({
    handleLoadMethod,
  }));

  useEffect(() => {
    setOpenSteps(loadComponents.length > 0 || true);
  }, [loadComponents, designVersionId]);
  useEffect(() => {
    const loadAPIData = _.find(
      selectedDesignAndDesignVersion.componentDTOList,
      {
        componentType: "ElectricalLoad",
      }
    );
    const isAshraePresent = loadAPIData?.datasource?.toLowerCase() === "ashrae";

    setDataSource(isAshraePresent ? "ashrae" : initialTab);
  }, [initialTab, designVersionId]);
  const resetAshraeLoadState = useCallback(() => {
    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      "useThermalLoad",
      false
    );
    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      "thermalFile",
      null
    );
    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      "isThermalFileClicked",
      false
    );
    updateLoadState(
      "componentAttributes.loadParameters.profileDetail",
      "startDate",
      new Date(
        new Date(new Date().setDate(new Date().getDate() - 1)).setFullYear(
          new Date().getFullYear() - 1
        )
      )
    );
  }, [updateLoadState]);

  const handleTabClick = (tab) => {
    if(!tab) return;
    if (tab !== "ashrae" && dataSource === "ashrae") {
      resetAshraeLoadState();
      prevDataSourceRef.current = tab;
    }
    setDataSource(tab);
    setOpenSteps(true);
    updateLoadState("datasource", null, tab);
    if (tab === "ashrae") {
      setComponentDetails((prev) => {
        const next = { ...prev, currentComponent: "load" };
        _.set(next, "load.componentAttributes.loadParameters.profileFile", []);
        _.set(next, "load.componentAttributes.loadParameters.profileDetail.loadFile", null);
        _.set(next, "load.componentAttributes.loadParameters.profileDetail.thermalFile", null);
        _.set(next, "load.componentAttributes.loadParameters.profileDetail.isLoadFileClicked", false);
        _.set(next, "load.componentAttributes.loadParameters.profileDetail.isThermalFileClicked", false);
        return next;
      });
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "useThermalLoad",
        true
      );
      if (!componentDetails?.load?.componentAttributes?.loadParameters?.scalingFactorElec) {
        updateLoadState(
          "componentAttributes.loadParameters",
          "scalingFactorElec",
          1
        );
      }
      if (!componentDetails?.load?.componentAttributes?.loadParameters?.scalingFactorTherm) {
        updateLoadState(
          "componentAttributes.loadParameters",
          "scalingFactorTherm",
          1
        );
      }
      if (!componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.ThermalEnergyPrice && componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.ThermalEnergyPrice !== 0) {
        updateLoadState(
          "componentAttributes.loadParameters.profileDetail",
          "ThermalEnergyPrice",
          0
        );
      }
      if (!componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.ThermalEmissionFactor && componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.ThermalEmissionFactor !== 0) {
        updateLoadState(
          "componentAttributes.loadParameters.profileDetail",
          "ThermalEmissionFactor",
          0
        );
      }
    }
  };

  useEffect(() => {
    if (prevDataSourceRef.current === "ashrae" && dataSource !== "ashrae") {
      resetAshraeLoadState();
    }
    prevDataSourceRef.current = dataSource;
  }, [dataSource, resetAshraeLoadState]);

  const header = () => (
    <span
      data-intro={t("loadMethod.loadStepIntro")}
      data-position="bottom"
    >
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("load") + 1}`}: A.
      </span>
      <span
        className={`ms-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >
        {t('loadMethod')}
      </span>
    </span>
  );

  // Use helper to temporarily remove sticky from accordion header containing data-intro during tour
  useRemoveStickyOnIntro(".rc-accordion-header", !!tour, {
    overridePosition: "static",
    useImportant: true,
  });

  const handleLoadUpload = async () => {
    setLoadingComp(true);
    const componentType =
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.useThermalLoad &&
      (componentDetails?.load?.componentAttributes?.loadParameters
        ?.profileDetail?.thermalFile ||
        isThermalPresent?.componentType)
        ? "ThermalLoad"
        : "ElectricalLoad";
    const filteredComponent =
      selectedDesignAndDesignVersion?.componentDTOList?.find(
        (comp) => comp.componentType === componentType
      );
    const currentUnits =
      componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail
        ?.units;

    const ashraeStartDate =
      form.values?.componentAttributes?.loadParameters?.profileDetail?.startDate;
    const ashraeYear = ashraeStartDate
      ? new Date(ashraeStartDate)?.getFullYear()
      : new Date().getFullYear() - 1;
    const ashraeStartIso = `${ashraeYear}-01-01T00:00:00.000Z`;

    const payload = {
      projectId: projectId,
      designId: designId,
      designVersionId: designVersionId,
      componentType: dataSource === "ashrae" ? "ElectricalLoad" : componentType,
      componentId: filteredComponent ? filteredComponent.componentId : -1,
      componentAttributes: {
        loadParameters: {
          profileDetail: {
            ...componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail,
            startDate:
              dataSource === "ashrae"
                ? ashraeStartIso
                : toUtcISOStringWithStartTime(
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.startDate,
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.startTime
                  ),
            profileDataLoc:
              dataSource === "ashrae"
                ? componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileDetail?.profileDataLoc
                : null,
            scalingFactor:
              dataSource === "ashrae"
                ? componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileDetail?.scalingFactor
                : null,
            useThermalLoad: componentDetails?.load?.componentAttributes?.loadParameters
                    ?.profileDetail?.useThermalLoad,
            ThermalEnergyPrice:(
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad)?
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.ThermalEnergyPrice:null,
            ThermalEmissionFactor:(
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad)?
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.ThermalEmissionFactor:null,
            powerFactor: componentDetails?.load?.componentAttributes?.loadParameters?.profileDetail?.powerFactor          
          },
            scalingFactorElec:
              dataSource === "ashrae"
                ? componentDetails?.load?.componentAttributes?.loadParameters
                    ?.scalingFactorElec
                : null,
            scalingFactorTherm:
              dataSource === "ashrae" &&
              componentDetails?.load?.componentAttributes?.loadParameters
                ?.profileDetail?.useThermalLoad
                ? componentDetails?.load?.componentAttributes?.loadParameters
                    ?.scalingFactorTherm
                : null,
          profileFile:
            dataSource === "ashrae"
              ? []
              : (() => {
                  const useThermal =
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.useThermalLoad;
                  const hasThermalFile =
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.thermalFile;
                  const hasLoadFile =
                    componentDetails?.load?.componentAttributes?.loadParameters
                      ?.profileDetail?.loadFile;
                  const thermalExists = !!isThermalPresent?.componentType;
                  const electricalExists =
                    !!isElectricalDataPresent?.componentType;
                  const isElectrical = componentType === "ElectricalLoad";

                  // If configuring ThermalLoad
                  if (!isElectrical && useThermal) {
                    if (hasThermalFile || !thermalExists) {
                      return componentDetails?.load?.componentAttributes
                        ?.loadParameters?.profileFile;
                    }
                    if (!hasThermalFile && thermalExists) {
                      return isThermalPresent?.componentAttributes
                        ?.loadParameters?.profileFile;
                    }
                  }

                  // If configuring ElectricalLoad
                  if (isElectrical && !useThermal) {
                    if (hasLoadFile || !electricalExists) {
                      return componentDetails?.load?.componentAttributes
                        ?.loadParameters?.profileFile;
                    }
                    if (!hasLoadFile && electricalExists) {
                      return isElectricalDataPresent?.componentAttributes
                        ?.loadParameters?.profileFile;
                    }
                  }

                  // Default fallback
                  return componentDetails?.load?.componentAttributes
                    ?.loadParameters?.profileFile;
                })(),
        },
      },
      datasource: dataSource === "ashrae" ? "Ashrae" : "Upload",
    };
    try {
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "loadFile",
        null
      );
      updateLoadState(
        "componentAttributes.loadParameters.profileDetail",
        "thermalFile",
        null
      );
          MGA_API.importLoad(payload)
        .then((resp) => {
          setLoadingComp(false);
          previousElectricalUnitsRef.current =
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.units;
          displayError("success", t('loadMethod.status.saveSuccess'));
          // Update projectDetails with the response if it contains componentAttributes
          if (resp?.componentId) {
            MGA_API.editProject(projectId).then((res) => {
              const newComponentDTOList =
                flattenCountComponentDTOList(res.countComponentDTOList) || [];

              // Get the matching design and design version for finding updatedLoadComponent
              const matchingDesign = res?.designComponentsList?.find((design) =>
                design?.designVersionComponents?.some(
                  (version) =>
                    String(version.designVersionId) === String(designVersionId)
                )
              );

              const matchingDesignVersion =
                matchingDesign?.designVersionComponents?.find(
                  (version) =>
                    String(version.designVersionId) === String(designVersionId)
                );

              const updatedLoadComponent =
                matchingDesignVersion?.componentDTOList?.find(
                  (comp) =>
                    comp.componentType ===
                    (dataSource === "ashrae" ? "ElectricalLoad" : componentType)
                );

              // Update projectDetails with fresh API response
              setProjectDetails((prev) => ({
                ...prev,
                userId: res.userId || "",
                currencyCode: res?.projectDTO?.currencyCode || "$",
                projectDTO: res.projectDTO || {},
                componentDTOList: newComponentDTOList,
                designWithVersionsDTOList: res.designWithVersionsDTOList || [],
                locationDTO: res.locationDTO || {},
                designComponentsList:
                  flattenDesignCountComponent(res?.designComponentsList) || [],
              }));

              // Check if hasBothLoads - calculate fresh from API response, not from getLoadInfo hook
              const freshDesignComponentsList =
                flattenDesignCountComponent(res?.designComponentsList) || [];
              const matchingDesignFromRes = freshDesignComponentsList?.find(
                (design) => String(design.designId) === String(designId)
              );

              const matchingVersionFromRes =
                matchingDesignFromRes?.designVersionComponents?.find(
                  (version) =>
                    String(version.designVersionId) === String(designVersionId)
                );

              const countComponentList =
                matchingVersionFromRes?.countComponentDTOList || [];
              const hasElectricalLoad = countComponentList.some(
                (c) => c.componentType === "ElectricalLoad"
              );
              const hasThermalLoad = countComponentList.some(
                (c) => c.componentType === "ThermalLoad"
              );
              const hasBothLoadsCalculated =
                hasElectricalLoad && hasThermalLoad;

              if (hasBothLoadsCalculated) {
                // Get both electrical and thermal load components from fresh data
                const electricalLoadComponent =
                  matchingVersionFromRes?.componentDTOList?.find(
                    (comp) => comp.componentType === "ElectricalLoad"
                  );

                const thermalLoadComponent =
                  matchingVersionFromRes?.componentDTOList?.find(
                    (comp) => comp.componentType === "ThermalLoad"
                  );
                setComponentStatus((prev) => ({
                  ...prev,
                  load: {
                    completed: true,
                    isEditing: false,
                  },
                }));

                // Fetch electrical load if exists and not already fetched
                if (
                  electricalLoadComponent?.componentId &&
                  resp.componentType !== "ElectricalLoad"
                ) {
                  MGA_API.getComponentInfo(
                    designId,
                    designVersionId,
                    electricalLoadComponent.componentId
                  )
                    .then((elecResp) => {
                      if (elecResp?.componentId) {
                        updateProjectDetailsWithComponentAttributes(
                          elecResp,
                          designVersionId
                        );
                      }
                    })
                    .catch((error) => {
                      console.log(
                        "Error fetching electrical load component:",
                        error
                      );
                      displayError('error', getErrorMessage(error));
                    });
                }

                // Fetch thermal load if exists and not already fetched
                if (thermalLoadComponent?.componentId) {
                  MGA_API.getComponentInfo(
                    designId,
                    designVersionId,
                    thermalLoadComponent.componentId
                  )
                    .then((thermResp) => {
                      if (thermResp?.componentId) {
                        updateProjectDetailsWithComponentAttributes(
                          thermResp,
                          designVersionId
                        );
                      }
                    })
                    .catch((error) => {
                      console.log(
                        "Error fetching thermal load component:",
                        error
                      );
                      displayError('error', getErrorMessage(error));
                    });
                }
              } else {
                updateProjectDetailsWithComponentAttributes(
                  resp,
                  designVersionId
                );
                setComponentStatus((prev) => ({
                  ...prev,
                  load: {
                    completed: true,
                    isEditing: false,
                  },
                }));
              }
              //       if (updatedLoadComponent) {
              // updatedLoadComponent.componentAttributes.loadParameters.profileDetail.startDate =
              //   updatedLoadComponent.componentAttributes.loadParameters
              //     .profileDetail.startDate
              //     ? new Date(
              //         updatedLoadComponent.componentAttributes?.loadParameters?.profileDetail?.startDate
              //       )
              //     : new Date(
              //         new Date(
              //           new Date().setDate(new Date().getDate() - 1)
              //         ).setFullYear(new Date().getFullYear() - 1)
              //       );
              // setComponentDetails((prev) => ({
              //   ...prev,
              //   load: updatedLoadComponent,
              // }));
              // }
            });
          }
        })
        .catch((error) => {
          setLoadingComp(false);
          displayError(
            "error",
            error.response?.data?.message || t('loadMethod.status.saveError')
          );
        });
      // MGA_API.editProject(
      //   projectId
      // ).then((res) => {
      //   const newComponentDTOList =
      //     flattenCountComponentDTOList(res.countComponentDTOList) || [];

      //   // Get the matching design and design version for finding updatedLoadComponent
      //   const matchingDesign = res?.designComponentsList?.find((design) =>
      //     design?.designVersionComponents?.some(
      //       (version) => String(version.designVersionId) === String(designVersionId)
      //     )
      //   );

      //   const matchingDesignVersion = matchingDesign?.designVersionComponents?.find(
      //     (version) => String(version.designVersionId) === String(designVersionId)
      //   );

      //   const updatedLoadComponent = matchingDesignVersion?.componentDTOList?.find(
      //     (comp) =>
      //       comp.componentType ===
      //       (dataSource === "ashrae" ? "ElectricalLoad" : componentType)
      //   );

      //   setProjectDetails((prev) => ({
      //     ...prev,
      //     userId: res.userId || "",
      //     currencyCode: res?.projectDTO?.currencyCode || "$",
      //     projectDTO: res.projectDTO || {},
      //     componentDTOList: newComponentDTOList,
      //     designWithVersionsDTOList: res.designWithVersionsDTOList || [],
      //     locationDTO: res.locationDTO || {},
      //     designComponentsList: flattenDesignCountComponent(res?.designComponentsList) || [],
      //   }));
      //   // Update componentsAtom with the latest load component
      //   if (updatedLoadComponent) {
      //     updatedLoadComponent.componentAttributes.loadParameters.profileDetail.startDate =
      //       updatedLoadComponent.componentAttributes.loadParameters
      //         .profileDetail.startDate
      //         ? new Date(
      //             updatedLoadComponent.componentAttributes?.loadParameters?.profileDetail?.startDate
      //           )
      //         : new Date(
      //             new Date(
      //               new Date().setDate(new Date().getDate() - 1)
      //             ).setFullYear(new Date().getFullYear() - 1)
      //           );
      //     setComponentDetails((prev) => ({
      //       ...prev,
      //       load: updatedLoadComponent,
      //     }));

      //     // Also update projectDetails using the updateProjectDetailsWithComponentAttributes function
      //     updateProjectDetailsWithComponentAttributes(updatedLoadComponent, designVersionId);
      //   }
      // });
    } catch (error) {
      setLoadingComp(false);
      const errorMessage = error.response?.data
        ? error.response?.data
        : t('loadMethod.status.uploadError');
      displayError("error", errorMessage.message);
    }
  };

  const loadBorderColor =
    colorScheme === "dark" ? "border-dark-radius-px" : "border-gray-radius-3px";
  const selectedCardBorder = "2px solid #3dcd58";
  const unselectedCardBorder = "2px solid #e0e0e0";
  const selectedBoxShadow = "0 0 0 2px #3dcd5833";
  const containerRef = useRef(null);
  const loadStepOne = useRef();
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);

  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  // Update projectDetails atom with componentAttributes from API response - same logic as loadProject
  const updateProjectDetailsWithComponentAttributes = useCallback(
    (apiResponse, designVersionId) => {
      // Search through designComponentsList to find the matching designVersionComponents
      setProjectDetails((prevDetails) => {
        const updatedDesignComponentsList =
          prevDetails?.designComponentsList?.map((design) => {
            // Find the matching design version
            const updatedDesignVersionComponents =
              design?.designVersionComponents?.map((version) => {
                if (
                  String(version.designVersionId) === String(designVersionId)
                ) {
                  // Found matching design version, now update the component in componentDTOList
                  const updatedComponentDTOList =
                    version?.componentDTOList?.map((component) => {
                      if (
                        String(component.componentId) ===
                        String(apiResponse?.componentId)
                      ) {
                        // Found matching component, update only componentAttributes while preserving previous data
                        return {
                          ...component,
                          // Only update componentAttributes, preserve all other component properties (componentId, componentType, etc)
                          componentAttributes: apiResponse?.componentAttributes,
                        };
                      }
                      return component;
                    });
                  return {
                    ...version,
                    componentDTOList:
                      updatedComponentDTOList || version?.componentDTOList,
                  };
                }
                return version;
              });
            return {
              ...design,
              designVersionComponents:
                updatedDesignVersionComponents ||
                design?.designVersionComponents,
            };
          });
        return {
          ...prevDetails,
          designComponentsList:
            updatedDesignComponentsList || prevDetails?.designComponentsList,
        };
      });
    },
    []
  );

  // Only run help logic if the event target is the container itself
  const handleContainerClick = (event) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector('[card-id="accordion-load-1"]');
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event);
    }
  };
  const children = () => (
    <div
      className="row mt-3"
      ref={containerRef}
      data-id="accordion-load-1"
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      role="button"
    >
      <div className="col-sm-12">
        <div
          className="row"
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignContent: "center",
            alignItems: "center",
            justifyContent: "center",
            gap: "24px",
            marginBottom: "24px",
            marginTop: "24px",
          }}
        >
          <Card
            shadow="sm"
            padding="xs"
            radius="md"
            data-intro={t("loadMethod.clickImportProfile")}
            data-position="bottom"
            data-id="field-load-card"
            onClick={(e) => {
              e.stopPropagation(); // Prevent bubbling to container
              handleTabClick("load");
              // Trigger help logic for card selection
              if (containerRef.current) {
                const cardElement = document.querySelector(
                  '[card-id="field-load-card"]'
                );
                if (cardElement) {
                  cardElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  cardElement.style.transition = "background-color 0.5s ease";
                  cardElement.style.border = "2px solid #42b4e6";
                  setTimeout(() => {
                    cardElement.style.border = "";
                  }, 2000);
                }
              }
            }}
            style={{
              flex: "0 1 500px",
              minWidth: "180px",
              maxWidth: "220px",
              height: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border:
                dataSource === "load" ||
                dataSource === "electric" ||
                dataSource === "thermal"
                  ? selectedCardBorder
                  : unselectedCardBorder,
              boxShadow:
                dataSource === "load" ||
                dataSource === "electric" ||
                dataSource === "thermal"
                  ? selectedBoxShadow
                  : undefined,
              transition: "border 0.2s, box-shadow 0.2s",
              wordBreak: "break-word",
              textAlign: "center",
              padding: "14px 12px",
            }}
          >
            <Text
              align="center"
              weight={600}
              size="xs"
              className="loadSubHeading mt-1 mb-1"
              style={{
                whiteSpace: "normal",
                fontSize: "16px",
                paddingBottom: "4px",
              }}
            >
              {t('load.importerTitle')}
            </Text>
            <Text
              align="center"
              size="xs"
              className="loadText mb-1"
              style={{ whiteSpace: "normal", fontSize: "12px" }}
            >
              {t('importLoad')}
            </Text>
          </Card>
          <Card
            shadow="sm"
            data-intro={t("loadMethod.clickImportASHRAE")}
            data-position="bottom"
            padding="xs"
            radius="md"
            data-id="field-ashrae-card"
            onClick={(e) => {
              e.stopPropagation(); // Prevent bubbling to container
              handleTabClick("ashrae");
              // Trigger help logic for card selection
              if (containerRef.current) {
                const cardElement = document.querySelector(
                  '[card-id="field-ashrae-card"]'
                );
                if (cardElement) {
                  cardElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  cardElement.style.transition = "background-color 0.5s ease";
                  cardElement.style.border = "2px solid #42b4e6";
                  setTimeout(() => {
                    cardElement.style.border = "";
                  }, 2000);
                }
              }
            }}
            style={{
              flex: "0 1 500px",
              minWidth: "180px",
              maxWidth: "220px",
              height: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border:
                dataSource === "ashrae"
                  ? selectedCardBorder
                  : unselectedCardBorder,
              boxShadow:
                dataSource === "ashrae" ? selectedBoxShadow : undefined,
              transition: "border 0.2s, box-shadow 0.2s",
              wordBreak: "break-word",
              textAlign: "center",
              padding: "14px 12px",
            }}
          >
            <Text
              align="center"
              weight={600}
              size="xs"
              className="loadSubHeading mt-1 mb-1"
              style={{
                whiteSpace: "normal",
                fontSize: "16px",
                paddingBottom: "4px",
              }}
            >
              {t('load.typicalBuildingTitle')}
            </Text>
            <Text
              align="center"
              size="xs"
              className="loadText mb-1"
              style={{ whiteSpace: "normal", fontSize: "12px" }}
            >
              {t('importAshry')}
            </Text>
          </Card>
        </div>
      </div>
    </div>
  );
  return (
    <div className="row">
      <div className="container-fluid ">
        <AccordionItem id={1} header={header()} key={1} children={children()} />

        <UploadMethod
          initialTab={dataSource}
          initialSelectedBuildingType={initialSelectedBuildingType}
          projectDetails={projectDetails}
          loadComponents={loadComponents}
          isThermalEnabled={
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.useThermalLoad
          }
          openSteps={openSteps}
          triggerSave={triggerSave}
          saveComponents={saveComponents}
          form={form}
          updateLoadState={updateLoadState}
          componentDetails={componentDetails}
          handleLoadUpload={handleLoadUpload}
          setLoadingComp={setLoadingComp}
        />
      </div>
    </div>
  );
});

LoadMethod.propTypes = {
  setLoadingComp: PropTypes.func,
  projectDetails: PropTypes.object,
  initialTab: PropTypes.string,
  initialSelectedBuildingType: PropTypes.string,
  loadComponents: PropTypes.array,
  saveComponents: PropTypes.func,
};

export default LoadMethod;
